// One row of icon buttons that every assistant result shares so the UI
// always looks the same: copy, download, regenerate. Each action is
// optional — pass only what makes sense for the renderer.
import { Copy, Download, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type Props = {
  copyText?: string;
  onDownload?: () => void | Promise<void>;
  downloadLabel?: string;
  onRegenerate?: () => void;
  className?: string;
  rightSlot?: React.ReactNode;
};

export default function ResultActions({
  copyText,
  onDownload,
  downloadLabel = "Download",
  onRegenerate,
  className,
  rightSlot,
}: Props) {
  return (
    <div className={cn("flex items-center gap-1 flex-wrap", className)}>
      {copyText !== undefined && (
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(copyText);
            toast.success("Copied");
          }}
          disabled={!copyText}
          className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/60 disabled:opacity-40"
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      )}
      {onDownload && (
        <button
          type="button"
          onClick={() => {
            try {
              const r = onDownload();
              if (r && typeof (r as Promise<void>).then === "function") {
                (r as Promise<void>).catch((e) =>
                  toast.error(e?.message ?? "Download failed"),
                );
              }
            } catch (e: any) {
              toast.error(e?.message ?? "Download failed");
            }
          }}
          className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-primary hover:bg-primary/10"
        >
          <Download className="w-3 h-3" /> {downloadLabel}
        </button>
      )}
      {onRegenerate && (
        <button
          type="button"
          onClick={onRegenerate}
          className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        >
          <RotateCw className="w-3 h-3" /> Regenerate
        </button>
      )}
      {rightSlot}
    </div>
  );
}